/**
 * AttributeTable — tabla de atributos map-céntrica (primitiva S.UI.P4).
 *
 * Presentacional puro: sin lógica de negocio, sin estado interno y sin consultar
 * datos. Recibe las filas (`filas`) y el estado de selección y de filtro, ambos
 * controlados por props, y emite callbacks (`onSeleccionar?`, `onFiltroEstado?`,
 * `onEditar?`); no geocodifica ni orquesta. Para la ranura `panelInf` de
 * `WorkspaceShell` (S.UI.P1); no se monta aún (DoD por primitiva — Bloque P de
 * PLAN_SIMPLIF_UI_P1P2P3). La sincronización mapa↔tabla es P5; aquí solo el
 * contrato controlado. La edición inline gateada por rol expone la afordancia
 * (botón por fila); el formulario real vive en el consumidor (montaje A3b). Skin
 * DS v2 (nombres en Space Grotesk; IDs, coordenadas y score en JetBrains Mono);
 * sin glow ni box-shadow (regla dura).
 *
 * P2RED-S1-2 · tabla canónica (adenda §11 CRITERIOS_SHELL):
 * - Chips de filtro CON contadores (§7: los contadores son el filtro).
 * - Ranura `cabecera` para alojar el buscador global junto a los chips (§6).
 * - Copiar-en-fila (hover/focus): escribe «X, Y» al portapapeles con los
 *   decimales de la tabla (§11.2); acciones visibles al pasar el puntero.
 * - El lápiz sustituye al botón «Editar»: abre la ficha, no edita in situ
 *   (§11.2); sigue gateado por rol `editor` (FB.10.17).
 *
 * VIS-3c · lámina de la tabla (DOP selladas 01-ago):
 * - Píldora de *score* tintada; el **nivel llega ya resuelto** por el consumidor
 *   (`DOP-VIS3C-UMBRALES`=a): la primitiva no conoce umbrales.
 * - Tipología en **columna propia** (`DOP-VIS3C-COLUMNA`=a), retirada del sufijo
 *   de la celda de nombre; el **color llega ya resuelto** (`DOP-VIS3C-CLAVE-TIPOLOGIA`=b).
 * - Sin tipología (dato ausente) → guion, igual que X/Y sin coordenada
 *   (`DOP-VIS3C-S2-HUECO`=a).
 *
 * @version 1.2.0
 */

import type { CSSProperties, FC, ReactNode } from 'react';
import { Copy, Pencil } from 'lucide-react';
import { colors, typography } from '@/design-system/v2';
import type { Coord25830 } from './StatusBar';
import { ETIQUETA_ESTADO_FILA, type EstadoUbicacionFila } from '../shared/estadoFila';

// ── Tokens DS v2 ─────────────────────────────────────────────────────────────
const COLORS = {
  bg: colors.bg.secondary,
  fgFuerte: colors.fg.primary,
  fgTenue: colors.fg.muted,
  borde: colors.border.DEFAULT,
  seleccion: colors.primary, // #2dd4bf — fila seleccionada
  ubicada: colors.success, // #a3e635
  porRevisar: colors.warning, // #fbbf24
  sinCoordenada: colors.error, // #f87171
} as const;

const DECIMALES_DEF = 2;

// ── Tipos ────────────────────────────────────────────────────────────────────
/** Estado presentacional de una fila: los tres de `shared/estadoFila` (P2VOC-b). */
export type EstadoFila = EstadoUbicacionFila;

/** Valor del filtro por estado: un estado concreto o 'todos'. */
export type FiltroEstado = EstadoFila | 'todos';

/** Rol del consumidor: 'lectura' (solo ver) o 'editor' (edición por fila). */
export type RolTabla = 'lectura' | 'editor';

/**
 * Nivel de *score* **ya resuelto por el consumidor** (`DOP-VIS3C-UMBRALES`=a).
 * La primitiva NO conoce umbrales: recibe el nivel y pinta. La resolución vive
 * en `adapters.ts`, contra el catálogo `colors.scores` del DS v2.
 */
export type NivelScore = 'alto' | 'medio' | 'bajo' | 'critico';

/** Modelo presentacional mínimo de una fila de la tabla de atributos. */
export interface FilaAtributo {
  /** Identificador estable de la fila (se muestra en mono). */
  id: string;
  /** Nombre legible (se muestra en sans, con elipsis). */
  nombre: string;
  /** Estado de ubicación de la fila. */
  estado: EstadoFila;
  /** Coordenada EPSG:25830 (se muestra en mono); ausente si sin geolocalizar. */
  coord?: Coord25830;
  /** Tipología legible del documento (columna propia; ausente si el dato falta). */
  tipologia?: string;
  /**
   * Color de identidad de la macro-tipología, **ya resuelto por el consumidor**
   * (`DOP-VIS3C-CLAVE-TIPOLOGIA`=b). La primitiva no indexa la simbología.
   */
  tipologiaColor?: string;
  /** Puntuación de validación 0–100 (se muestra en mono). */
  score?: number;
  /** Nivel del *score* ya resuelto; sin él la cifra se pinta sin tintar. */
  scoreNivel?: NivelScore;
}

export interface AttributeTableProps {
  /** Filas a mostrar (orden de arriba abajo). */
  filas: FilaAtributo[];
  /**
   * Contenido de cabecera (típicamente el buscador global, §6 CRITERIOS_SHELL)
   * alojado a la izquierda de los chips de filtro. La semántica del buscador
   * («localiza y selecciona», no filtra) la cablea el consumidor.
   */
  cabecera?: ReactNode;
  /** Id de la fila seleccionada (selección controlada por el consumidor). */
  idSeleccion?: string;
  /** Callback al seleccionar una fila (clic). */
  onSeleccionar?: (id: string) => void;
  /** Filtro de estado activo (controlado). Def: 'todos'. */
  filtroEstado?: FiltroEstado;
  /** Callback de filtro; si se omite, no se muestran los chips de filtro. */
  onFiltroEstado?: (filtro: FiltroEstado) => void;
  /** Rol del consumidor; 'editor' habilita la edición por fila. Def: 'lectura'. */
  rol?: RolTabla;
  /** Callback de edición por fila (solo visible en rol 'editor'). */
  onEditar?: (id: string) => void;
  /** Decimales de X/Y (sin separador de miles, convención QGIS). Def: 2. */
  decimales?: number;
  /** Clase CSS adicional. */
  className?: string;
}

// ── Etiquetas, colores y orden de estados ────────────────────────────────────
const ESTADO_LABEL: Record<EstadoFila, string> = ETIQUETA_ESTADO_FILA;
const ESTADO_COLOR: Record<EstadoFila, string> = {
  ubicada: COLORS.ubicada, por_revisar: COLORS.porRevisar, sin_coordenada: COLORS.sinCoordenada,
};
/**
 * VIS-3b: fondo tintado del badge de estado (mockup P2_REDISENO v1, regla
 * `.st.*` al 14 %). Se deriva de `ESTADO_COLOR`, NO del nombre de la clase del
 * mockup: allí `ubi` es ámbar y aquí `ubicada` es lima — los colores coinciden
 * por posición, no por nombre, y mapear por nombre invertiría la semántica.
 */
const ESTADO_BG: Record<EstadoFila, string> = {
  ubicada: 'rgba(163,230,53,.14)',
  por_revisar: 'rgba(251,191,36,.14)',
  sin_coordenada: 'rgba(248,113,113,.14)',
};
const FILTROS: FiltroEstado[] = ['todos', 'ubicada', 'por_revisar', 'sin_coordenada'];
const FILTRO_LABEL: Record<FiltroEstado, string> = {
  todos: 'Todas', ubicada: 'Ubicadas', por_revisar: 'Por revisar', sin_coordenada: 'Sin coordenada',
};

// ── Formato (presentación pura, sin separador de miles — convención QGIS) ─────
const formatearValor = (v: number, decimales: number): string => v.toFixed(decimales);

/** Texto de copia de la fila: «X, Y» con los decimales de la tabla (§11.2). */
const textoCopiaCoord = (coord: Coord25830, decimales: number): string =>
  `${formatearValor(coord.x, decimales)}, ${formatearValor(coord.y, decimales)}`;

/**
 * CSS de las acciones por fila (copiar/lápiz): ocultas hasta hover o
 * focus-within (accesibilidad por teclado). Inyectado una vez con el
 * componente; sin glow (regla dura).
 */
const CSS_ACCIONES = `
.ptel-attribute-table .fila-acciones { opacity: 0; transition: opacity 0.12s ease; }
.ptel-attribute-table tr:hover .fila-acciones,
.ptel-attribute-table tr:focus-within .fila-acciones { opacity: 1; }
`;

// ── Estilos ──────────────────────────────────────────────────────────────────
const raizStyle: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: '6px', width: '100%', minHeight: 0,
  padding: '8px 14px', background: COLORS.bg, color: COLORS.fgFuerte,
  fontFamily: typography.fontFamily.sans, fontSize: typography.fontSize.sm,
};
const cabeceraStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
};
const filtrosStyle: CSSProperties = { display: 'flex', gap: '4px', flexWrap: 'wrap' };
const chipContadorStyle: CSSProperties = {
  fontFamily: typography.fontFamily.mono, marginLeft: '4px',
};
const chipStyle = (activo: boolean): CSSProperties => ({
  appearance: 'none', border: `1px solid ${activo ? COLORS.seleccion : COLORS.borde}`,
  borderRadius: '99px', background: 'transparent',
  color: activo ? COLORS.seleccion : COLORS.fgTenue,
  fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs,
  padding: '2px 9px', cursor: 'pointer', // sin glow
});
const tablaStyle: CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: typography.fontSize.sm,
};
const thStyle: CSSProperties = {
  textAlign: 'left', padding: '8px 12px', color: COLORS.fgTenue,
  fontSize: typography.fontSize['2xs'], fontWeight: typography.fontWeight.medium,
  letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
  borderBottom: `1px solid ${COLORS.borde}`,
};
const thNumStyle: CSSProperties = { ...thStyle, textAlign: 'right' };
const filaStyle = (seleccionada: boolean): CSSProperties => ({
  cursor: 'pointer',
  background: seleccionada ? colors.bg.tertiary : 'transparent',
  outline: seleccionada ? `1px solid ${COLORS.seleccion}` : 'none', // sin glow/box-shadow
});
const tdStyle: CSSProperties = {
  padding: '6px 12px', borderBottom: `1px solid ${COLORS.borde}`, verticalAlign: 'middle',
};
const tdNombreStyle: CSSProperties = {
  ...tdStyle, maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const tdMonoStyle: CSSProperties = {
  ...tdStyle, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs,
  textAlign: 'right', color: COLORS.fgTenue,
};
/**
 * RBAN (`OBS-P2-COLUMNA-TIPO-SIN-ANCHO-UTIL`): el ID era la única columna sin
 * restricción de ancho, luego bajo `table-layout: auto` cobraba su ancho
 * natural (27 caracteres de identificador) y dejaba a «Tipo» las sobras. Ahora
 * encoge con elipsis como Nombre: es el dato que la tolera, porque no se lee.
 */
const tdIdStyle: CSSProperties = {
  ...tdStyle, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs,
  color: COLORS.fgTenue,
  maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
/**
 * RBAN: suelo de ancho para que «Tipo» no vuelva a caer a ~7 caracteres cuando
 * las tres columnas encogibles se reparten el espacio sobrante. El suelo manda
 * sobre `maxWidth: 0`; la elipsis sigue actuando más allá de él.
 */
const tdTipoStyle: CSSProperties = {
  ...tdStyle, fontSize: typography.fontSize.xs,
  maxWidth: 0, minWidth: '12ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
/**
 * VIS-3c: píldora de *score*. Fondo tintado del catálogo `colors.scoresBg` del
 * DS v2 (mismo alfa que el badge de estado); el nivel llega ya resuelto. Sin
 * nivel, la cifra se pinta sin tintar (no se inventa un umbral aquí).
 */
const SCORE_FG: Record<NivelScore, string> = {
  alto: colors.scores.high, medio: colors.scores.medium,
  bajo: colors.scores.low, critico: colors.scores.critical,
};
const SCORE_BG: Record<NivelScore, string> = {
  alto: colors.scoresBg.high, medio: colors.scoresBg.medium,
  bajo: colors.scoresBg.low, critico: colors.scoresBg.critical,
};
const pildoraScoreStyle = (nivel?: NivelScore): CSSProperties => ({
  display: 'inline-block',
  fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs,
  fontWeight: typography.fontWeight.semibold,
  color: nivel != null ? SCORE_FG[nivel] : COLORS.fgTenue,
  background: nivel != null ? SCORE_BG[nivel] : 'transparent',
  padding: '2px 9px', borderRadius: '6px', // sin glow
});
const estadoStyle = (estado: EstadoFila): CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '4px',
  color: ESTADO_COLOR[estado], background: ESTADO_BG[estado],
  fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize['2xs'],
  fontWeight: typography.fontWeight.semibold,
  padding: '2px 7px', borderRadius: '99px',
});
const puntoStyle = (estado: EstadoFila): CSSProperties => ({
  width: '8px', height: '8px', borderRadius: '50%',
  background: ESTADO_COLOR[estado], flexShrink: 0,
});
const accionesCeldaStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '4px',
};
const accionBotonStyle: CSSProperties = {
  appearance: 'none', border: `1px solid ${COLORS.borde}`, borderRadius: '4px',
  background: 'transparent', color: COLORS.fgTenue,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '22px', height: '22px', padding: 0, cursor: 'pointer', // sin glow
};
const vacioStyle: CSSProperties = {
  padding: '8px 6px', color: COLORS.fgTenue, fontStyle: 'italic', fontSize: typography.fontSize.xs,
};
const guionStyle: CSSProperties = { color: COLORS.fgTenue };

// ── Subcomponente: fila de la tabla ──────────────────────────────────────────
const FilaTabla: FC<{
  fila: FilaAtributo;
  seleccionada: boolean;
  editable: boolean;
  decimales: number;
  onSeleccionar?: (id: string) => void;
  onEditar?: (id: string) => void;
}> = ({ fila, seleccionada, editable, decimales, onSeleccionar, onEditar }) => {
  const { id, nombre, estado, coord, tipologia, tipologiaColor, score, scoreNivel } = fila;
  return (
    <tr
      data-testid={`attribute-table-fila-${id}`}
      aria-selected={seleccionada}
      style={filaStyle(seleccionada)}
      onClick={() => onSeleccionar?.(id)}
    >
      <td style={tdStyle}>
        <span data-testid={`attribute-table-estado-${id}`} style={estadoStyle(estado)}>
          <span aria-hidden="true" style={puntoStyle(estado)} />
          {ESTADO_LABEL[estado]}
        </span>
      </td>
      <td style={tdNombreStyle} title={nombre}>
        <span>{nombre}</span>
      </td>
      <td
        data-testid={`attribute-table-tipologia-${id}`}
        style={tdTipoStyle}
        title={tipologia ?? undefined}
      >
        {tipologia != null
          ? <span style={{ color: tipologiaColor ?? COLORS.fgTenue }}>{tipologia}</span>
          : <span style={guionStyle}>—</span>}
      </td>
      <td data-testid={`attribute-table-id-${id}`} style={tdIdStyle}>{id}</td>
      <td data-testid={`attribute-table-x-${id}`} style={tdMonoStyle}>
        {coord != null ? formatearValor(coord.x, decimales) : <span style={guionStyle}>—</span>}
      </td>
      <td data-testid={`attribute-table-y-${id}`} style={tdMonoStyle}>
        {coord != null ? formatearValor(coord.y, decimales) : <span style={guionStyle}>—</span>}
      </td>
      <td data-testid={`attribute-table-score-${id}`} style={tdMonoStyle}>
        {score != null
          ? <span style={pildoraScoreStyle(scoreNivel)}>{String(score)}</span>
          : <span style={guionStyle}>—</span>}
      </td>
      <td style={tdStyle}>
        <span className="fila-acciones" style={accionesCeldaStyle}>
          {coord != null && (
            <button
              type="button"
              data-testid={`attribute-table-copiar-${id}`}
              aria-label={`Copiar coordenada de ${nombre}`}
              title="Copiar coordenada (X, Y)"
              style={accionBotonStyle}
              onClick={(e) => {
                e.stopPropagation();
                void navigator.clipboard?.writeText(textoCopiaCoord(coord, decimales));
              }}
            >
              <Copy size={12} aria-hidden="true" />
            </button>
          )}
          {editable && (
            <button
              type="button"
              data-testid={`attribute-table-editar-${id}`}
              aria-label={`Abrir ficha de ${nombre}`}
              title="Abrir la ficha del elemento"
              style={accionBotonStyle}
              onClick={(e) => { e.stopPropagation(); onEditar?.(id); }}
            >
              <Pencil size={12} aria-hidden="true" />
            </button>
          )}
        </span>
      </td>
    </tr>
  );
};

// ── Componente ───────────────────────────────────────────────────────────────
export const AttributeTable: FC<AttributeTableProps> = ({
  filas, cabecera, idSeleccion, onSeleccionar, filtroEstado = 'todos', onFiltroEstado,
  rol = 'lectura', onEditar, decimales = DECIMALES_DEF, className,
}) => {
  const editable = rol === 'editor';
  const visibles = filtroEstado === 'todos'
    ? filas
    : filas.filter((f) => f.estado === filtroEstado);
  // §7: los contadores de la cabecera SON el filtro (se calculan del total, no
  // del subconjunto visible, para que no cambien al filtrar).
  const contadores: Record<FiltroEstado, number> = {
    todos: filas.length,
    ubicada: filas.filter((f) => f.estado === 'ubicada').length,
    por_revisar: filas.filter((f) => f.estado === 'por_revisar').length,
    sin_coordenada: filas.filter((f) => f.estado === 'sin_coordenada').length,
  };
  const claseRaiz = className ? `ptel-attribute-table ${className}` : 'ptel-attribute-table';
  return (
    <div className={claseRaiz} data-testid="attribute-table" style={raizStyle}>
      <style>{CSS_ACCIONES}</style>
      {(cabecera != null || onFiltroEstado != null) && (
        <div data-testid="attribute-table-cabecera" style={cabeceraStyle}>
          {cabecera}
          {onFiltroEstado != null && (
            <div
              data-testid="attribute-table-filtros"
              role="group"
              aria-label="Filtrar por estado"
              style={filtrosStyle}
            >
              {FILTROS.map((f) => (
                <button
                  key={f}
                  type="button"
                  data-testid={`attribute-table-filtro-${f}`}
                  aria-pressed={filtroEstado === f}
                  style={chipStyle(filtroEstado === f)}
                  onClick={() => onFiltroEstado(f)}
                >
                  {FILTRO_LABEL[f]}{' '}
                  <span style={chipContadorStyle}>{contadores[f]}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {visibles.length === 0 ? (
        <p data-testid="attribute-table-vacio" style={vacioStyle}>No hay registros que mostrar.</p>
      ) : (
        <table data-testid="attribute-table-tabla" style={tablaStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Estado</th>
              <th style={thStyle}>Nombre</th>
              <th style={thStyle}>Tipo</th>
              <th style={thStyle}>ID</th>
              <th style={thNumStyle}>X</th>
              <th style={thNumStyle}>Y</th>
              <th style={thNumStyle}>Score</th>
              <th style={thStyle} aria-label="Acciones de fila" />
            </tr>
          </thead>
          <tbody>
            {visibles.map((fila) => (
              <FilaTabla
                key={fila.id}
                fila={fila}
                seleccionada={fila.id === idSeleccion}
                editable={editable}
                decimales={decimales}
                onSeleccionar={onSeleccionar}
                onEditar={onEditar}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AttributeTable;
